"use client";
import { Loading } from "@/components/ui/loading";
import { trackUserSignup } from "@/lib/gtm";
import { useSearchParams } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";

interface IOutsetaUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  [key: string]: string | undefined | { Name?: string } | { name?: string };
  Account?: {
    Name?: string;
  };
  organization?: {
    name?: string;
  };
}

interface IOutsetaOptions {
  widgetMode?: string;
  authenticationCallbackUrl?: string;
  tab?: string;
  [key: string]: string | undefined;
}

interface ISessionData {
  user: IOutsetaUser | null;
  isLoading: boolean;
  logout: (event: React.MouseEvent) => Promise<void>;
  openLogin: (options?: IOutsetaOptions) => void;
  openSignup: (options?: IOutsetaOptions) => void;
  openProfile: (options?: IOutsetaOptions) => void;
}

// Define Outseta interface
interface IOutsetaAuth {
  open: (options: IOutsetaOptions) => void;
  logout: () => void;
}

interface IOutsetaProfile {
  open: (options: IOutsetaOptions) => void;
}

interface IOutsetaInstance {
  getUser: () => Promise<IOutsetaUser>;
  getAccessToken: () => string | null;
  setAccessToken: (token: string | null) => void;
  on: (event: string, callback: () => void) => void;
  auth: IOutsetaAuth;
  profile: IOutsetaProfile;
  logout: (event: Event) => void;
}

// Function to get access token from cookies
function getAccessTokenFromCookie(): string | null {
  if (typeof document === "undefined") return null;

  const cookies = document.cookie.split(";");
  console.log("cookies", cookies);
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    // Get the cookie name and value
    const [name, value] = cookie.split("=", 2);

    // Check if this is the session cookie
    if (name === "session") {
      try {
        // Decode the URI component to get the JSON string
        const decodedValue = decodeURIComponent(value);
        // Parse the JSON
        const parsedValue = JSON.parse(decodedValue);
        // Return the accessToken property from the parsed object
        return parsedValue.accessToken || null;
      } catch (error) {
        console.error("Error parsing session cookie:", error);
        return null;
      }
    }
  }
  return null;
}

function getOutseta(): IOutsetaInstance | null {
  if (typeof window !== "undefined" && window["Outseta"]) {
    console.log("Outseta is loaded", window["Outseta"]);
    return window["Outseta"] as unknown as IOutsetaInstance;
  } else {
    console.warn("Outseta is not available yet, will retry");
    return null;
  }
}

const SessionContext = createContext<ISessionData | null>(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === null) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};

interface ISessionProviderProps {
  children: React.ReactNode;
}

export const Session = ({ children }: ISessionProviderProps) => {
  const searchParams = useSearchParams();
  const [status, setStatus] = useState("init");
  const [user, setUser] = useState<IOutsetaUser | null>(null);
  const [outseta, setOutseta] = useState<IOutsetaInstance | null>(null);
  const [outsetaLoaded, setOutsetaLoaded] = useState(false);
  const [loadingAttempts, setLoadingAttempts] = useState(0);
  const [hasTrackedSignup, setHasTrackedSignup] = useState(false);

  // Check for Outseta availability
  useEffect(() => {
    const checkOutseta = () => {
      const outsetaInstance = getOutseta();
      if (outsetaInstance) {
        setOutseta(outsetaInstance);
        setOutsetaLoaded(true);
        return true;
      }
      return false;
    };

    // Try immediately
    if (checkOutseta()) return;

    // If not available, set up a retry mechanism
    const interval = setInterval(() => {
      setLoadingAttempts((prev) => prev + 1);
      if (checkOutseta()) {
        clearInterval(interval);
      }
      // After 20 attempts (10 seconds), stop trying to avoid infinite loop
      if (loadingAttempts > 20) {
        clearInterval(interval);
        console.error("Failed to load Outseta after multiple attempts");
      }
    }, 500);

    return () => clearInterval(interval);
  }, [loadingAttempts]);

  useEffect(() => {
    // Only proceed if Outseta is loaded
    if (!outsetaLoaded || !outseta) return;

    console.log("outseta.access_token", getAccessTokenFromCookie());
    outseta
      .getUser()
      .then((user) => setUser(user))
      .catch((err) => console.error(err));

    const handleOutsetaUserEvents = (onEvent: () => void) => {
      outseta.setAccessToken(getAccessTokenFromCookie());
      outseta.on("subscription.update", onEvent);
      outseta.on("profile.update", onEvent);
      outseta.on("account.update", onEvent);
    };

    const updateUser = async () => {
      setStatus("loading");
      try {
        const outsetaUser = await outseta.getUser();
        console.log("outsetaUser", outsetaUser);
        setUser(outsetaUser);

        // Track signup event if this is a new signup and we haven't tracked it yet
        const isNewSignup = searchParams.get("signup") === "true" || searchParams.get("signup") === "success";
        if (isNewSignup && !hasTrackedSignup && outsetaUser) {
          trackUserSignup({
            email: outsetaUser.email,
            name: [outsetaUser.firstName, outsetaUser.lastName].filter(Boolean).join(" ")
          });
          setHasTrackedSignup(true);
          console.log("Signup event tracked for:", outsetaUser.email);
        }
      } catch (error) {
        console.error("Failed to get user:", error);
        setUser(null);
      } finally {
        setStatus("ready");
      }
    }; // Call updateUser only once
    updateUser();

    // Handle auth events
    const handleAuthEvent = () => {
      if (outseta.getAccessToken()) {
        updateUser();
      } else {
        setUser(null);
        setStatus("ready");
      }
    };

    // Listen for auth changes
    window.addEventListener("outseta-auth-changed", handleAuthEvent);

    // Set up user event handlers
    handleOutsetaUserEvents(updateUser);

    // Handle access token from URL
    const accessToken = searchParams.get("access_token");
    if (accessToken) {
      outseta.setAccessToken(accessToken);
      handleAuthEvent();
    } else if (outseta.getAccessToken()) {
      handleAuthEvent();
    } else {
      setStatus("ready");
    }

    return () => {
      handleOutsetaUserEvents(() => {});
      window.removeEventListener("outseta-auth-changed", handleAuthEvent);
    };
  }, [outsetaLoaded, outseta, searchParams, hasTrackedSignup]);

  const logout = async (event: React.MouseEvent) => {
    try {
      if (!outseta) {
        throw new Error("Outseta is not initialized");
      }

      // Call Outseta's logout with the event
      outseta.logout(event as unknown as Event);

      // Clear our local state
      setUser(null);
      setStatus("ready");
    } catch (error) {
      console.error("Error during logout:", error);
      // If logout fails, try to clean up and redirect
      setUser(null);
      setStatus("ready");
      window.location.href = "/";
    }
  };

  const openLogin = (options?: IOutsetaOptions) => {
    if (outseta) {
      outseta.auth.open({
        widgetMode: "login|register",
        authenticationCallbackUrl: window.location.href,
        ...options
      });
    }
  };

  const openSignup = (options?: IOutsetaOptions) => {
    if (outseta) {
      outseta.auth.open({
        widgetMode: "register",
        authenticationCallbackUrl: window.location.href,
        ...options
      });
    }
  };

  const openProfile = (options?: IOutsetaOptions) => {
    if (outseta) {
      outseta.profile.open({ tab: "profile", ...options });
    }
  };

  // If Outseta is still loading after a few attempts, show a loading indicator
  if (!outsetaLoaded && loadingAttempts > 2) {
    return <Loading message="Initializing authentication..." />;
  }

  return (
    <SessionContext.Provider
      value={{
        user,
        isLoading: status !== "ready",
        logout,
        openLogin,
        openSignup,
        openProfile
      }}
      data-oid="bwv0o26"
    >
      {children}
    </SessionContext.Provider>
  );
};
